import { useState } from 'react';
import { Section, Media } from '../ui';
import { TEAM, CAREERS_EMAIL } from '../content';

const VALUES = [
  { t: 'Operators first', d: 'Every capability started as a job we had to complete in the field.' },
  { t: 'Measured, not claimed', d: 'Model performance is reported against reviewed ground truth.' },
  { t: 'Built to hand off', d: 'Deliverables land in the systems your teams already work in.' }
];

// Bios run long, so each card shows the opening and expands in place rather
// than pushing the grid to four screens on a phone.
function Person({ p }) {
  const [open, setOpen] = useState(false);
  const short = p.bio.length > 190 ? `${p.bio.slice(0, 190).trimEnd()}…` : p.bio;
  return (
    <div>
      <Media src={p.img} alt={`${p.name}, ${p.role}`} ratio="1x1" ticked={false} />
      <div className="person__meta">
        <div className="person__name">{p.name}</div>
        <div className="person__role">{p.role}</div>
        <p className="person__bio">{open ? p.bio : short}</p>
        {p.bio.length > 190 && (
          <button className="bio-toggle" type="button" onClick={() => setOpen((v) => !v)}>
            {open ? 'Show less' : 'Read full bio'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function About() {
  return (
    <main>
      <Section lg>
        <span className="eyebrow">About</span>
        <h1 className="h-page" style={{ maxWidth: '20ch' }}>
          Field operations and computer vision, in the same company.
        </h1>
        <p className="lede" style={{ maxWidth: '58ch' }}>
          Most teams do one or the other. Wanda flies the missions and builds the models, which
          means the imagery is captured for the analysis it will feed — and the analysis is designed
          for imagery that actually exists.
        </p>
      </Section>

      <Section>
        <div className="split">
          <div>
            <span className="eyebrow">Our story</span>
            <h2 className="h-sub" style={{ margin: '16px 0 24px' }}>
              Built from experience. Driven by curiosity.
            </h2>
            <p className="body" style={{ fontSize: 16, marginBottom: 18 }}>
              Wanda Analytics began with a group of engineers, technologists, operators, and problem
              solvers who had spent years working together on complex, real-world challenges.
            </p>
            <p className="body" style={{ fontSize: 16, marginBottom: 18 }}>
              When our paths changed, we faced a choice: move on separately, or take what we had
              learned and build something of our own. We chose to build.
            </p>
            <p className="body" style={{ fontSize: 16 }}>
              We bootstrapped Wanda around a shared belief that the best technology starts with
              understanding the problem — the freedom to experiment, invent, and pursue ideas we
              believed could make a meaningful difference, without losing sight of the people behind
              the work.
            </p>
          </div>
          <Media
            src="/assets/team-meeting.jpg"
            alt="The Wanda Analytics team working through a problem around a conference table"
            ratio="4x3"
            ticked={false}
          />
        </div>
      </Section>

      <Section alt>
        <div className="grid-rule" style={{ '--min': '260px' }}>
          {VALUES.map((v) => (
            <div className="card card--flat" key={v.t}>
              <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>{v.t}</h3>
              <p className="body body--sm">{v.d}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <div className="sec-head">
          <h2 className="h-sub">Meet the team</h2>
          <span className="eyebrow">Leadership</span>
        </div>
        <p className="body" style={{ fontSize: 16, maxWidth: '58ch', marginTop: -24, marginBottom: 40 }}>
          Our team brings together experience in engineering, aviation, infrastructure operations,
          computer vision, and applied AI.
        </p>
        <div className="grid-plain" style={{ '--min': '230px' }}>
          {TEAM.map((p) => (
            <Person key={p.name} p={p} />
          ))}
        </div>
      </Section>

      <Section alt>
        <span className="eyebrow">Join our team</span>
        <h2 className="h-sub" style={{ margin: '16px 0 16px' }}>
          Shape your future.
        </h2>
        <p className="body" style={{ fontSize: 16, maxWidth: '54ch', marginBottom: 24 }}>
          Empower your career with a dynamic and innovative team. We are always seeking passionate
          individuals who are eager to make a difference in the field of image and video analytics.
          Reach out to explore current and future openings.
        </p>
        <a className="btn btn--ghost" href={`mailto:${CAREERS_EMAIL}`}>
          {CAREERS_EMAIL}
        </a>
      </Section>
    </main>
  );
}
